import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import ShellTooltip from "Components/common/ShellTooltip";
import { observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import { ReactComponent as PricingIcon } from "Global/Header/Icons/pricing.svg";
import OnBoardingRightSelection from "Components/onBoarding/OnBoardingRightSelection";
import SparkleIcon from "Components/common/SparkleIcon";
import { onboarding } from "Components/common/Onboarding";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import { currentSurface } from "Pages/Dashboard/Copilot/agent/copilotContext";
import Utils from "Utils/utils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import { REQUEST_DEMO_CALENDAR_LINK } from "Static/constant";
import watchOverlays from "Utils/overlayDim";

/*
 * The right rail: a thin, always-present strip of app-level actions.
 *
 * It exists so the copilot has a permanent home. A button buried among the
 * header icons reads as one more tool; a rail that is always there, with the
 * panel opening beside it, reads as part of the application. Pricing,
 * onboarding and Request Demo moved here with it -- they are the same kind of
 * thing (about the product, not about the page) and the header is for the
 * page's own controls.
 */

const RAIL_CLASS = "sgHasRightRail";

const RightRail = observer(() => {
  const history = useHistory();
  // The full-page copilot is its own surface; a rail offering to open the
  // drawer on top of it would be two copilots arguing over one conversation.
  const onCopilotPage = useLocation().pathname.startsWith("/copilot");

  // The rail takes real width out of the page, and .mainContent is the element
  // that has to give it up -- same mechanism the pinned drawer uses.
  useEffect(() => {
    const root = document.body;
    root.classList.add(RAIL_CLASS);
    const stopWatchingOverlays = watchOverlays();
    return () => {
      root.classList.remove(RAIL_CLASS);
      stopWatchingOverlays();
    };
  }, []);

  const track = event => Utils.mixpanelTrack(event, { pageType: PAGE_TYPE.header });

  const toggleCopilot = () => {
    const { surface, entityId } = currentSurface();
    copilotDrawerStore.toggle(surface, entityId);
  };

  // Portalled to <body> for the same reason the drawer is: .mainContent sets a
  // transform, which makes it the containing block for its fixed descendants --
  // so a rail rendered inside it would pin to the right edge of the PAGE AREA
  // (already narrowed by the drawer) instead of the viewport.
  return ReactDOM.createPortal(
    <nav className="sgRightRail" aria-label="Assistant and product">
      {!onCopilotPage && (
        <ShellTooltip label="Copilot" placement="left">
        <button
          type="button"
          className={`sgRailBtn sgRailBtn--copilot${
            copilotDrawerStore.open ? " isOn" : ""
          }`}
          aria-pressed={copilotDrawerStore.open}
          aria-label="Copilot"
          onClick={toggleCopilot}
        >
          <SparkleIcon />
        </button>
        </ShellTooltip>
      )}


      <ShellTooltip label="Pricing" placement="left">
      <button
        type="button"
        className="sgRailBtn"
        aria-label="Pricing"
        onClick={() => {
          track(MXP_EVENT.PRICING_CLICK_HEADER);
          history.push("/pricing");
        }}
      >
        <PricingIcon />
      </button>
      </ShellTooltip>

      <ShellTooltip label="Onboarding" placement="left">
      <button
        type="button"
        className="sgRailBtn"
        aria-label="Onboarding"
        onClick={() => {
          onboarding.setPopup(true);
          track(MXP_EVENT.ONB_SLIDER_CLICKED);
        }}
      >
        <OnBoardingRightSelection />
      </button>
      </ShellTooltip>

      <ShellTooltip label="Request Demo" placement="left">
      <button
        type="button"
        className="sgRailBtn sgRailBtn--demo"
        aria-label="Request Demo"
        onClick={() => {
          track(MXP_EVENT.REQUEST_DEMO_HEADER);
          Utils.windowRedirect(REQUEST_DEMO_CALENDAR_LINK, "_blank", true);
        }}
      >
        <i className="material-icons-outlined">calendar_today</i>
        <span className="sgRailBtn__label">Demo</span>
      </button>
      </ShellTooltip>
    </nav>,
    document.body
  );
});

export default RightRail;
